import { QueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import ky, { HTTPError } from 'ky';
import { AUTH_COOKIE_NAME, REFRESH_COOKIE_NAME, setAuthData, signOut } from '~/modules/auth/lib';

let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function refreshAuthToken() {
  if (isRefreshing) {
    return refreshPromise;
  }

  isRefreshing = true;

  const refreshToken = Cookies.get(REFRESH_COOKIE_NAME);

  if (!refreshToken) {
    signOut();
    isRefreshing = false;
    return Promise.reject(new Error('No refresh token available'));
  }

  refreshPromise = ky
    .post(`open/api/v1/sessions/refresh/${refreshToken}`, {
      prefix: window.__CONFIG.VITE_AUTH_API_URL,
      timeout: 30000,
      headers: {
        'X-Source': 'profile',
      },
    })
    .json<{ token: string; refreshToken: string }>()
    .then((result) => {
      console.log('Token refreshed!');

      setAuthData(result.token, result.refreshToken);
    })
    .catch((error) => {
      console.error(error);

      if (error instanceof HTTPError && error.response.status === 401) {
        signOut();
      }
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
}

function setAuthHeader(request: Request) {
  const authToken = Cookies.get(AUTH_COOKIE_NAME);

  if (authToken) {
    request.headers.set('User-Token', authToken);
  }
}

async function handleAuthRetry({ request, error }: { request: Request; error: unknown }) {
  const response = (error as HTTPError | undefined)?.response;

  if (response?.status === 401) {
    await refreshAuthToken();

    // `beforeRequest` hooks run only for the initial request, not for retries.
    // Re-read the (fresh) cookie and update the header, otherwise the retry
    // goes out with the old, already-expired token.
    setAuthHeader(request);
  }
}

export const profileApi = ky.create({
  prefix: window.__CONFIG.VITE_PROFILE_API_URL,
  timeout: 70000,
  retry: {
    limit: 1,
    methods: ['get', 'post', 'put', 'delete'],
    statusCodes: [401],
  },
  headers: {
    'X-Source': 'profile',
  },
  hooks: {
    beforeRequest: [({ request }) => setAuthHeader(request)],
    beforeRetry: [handleAuthRetry],
  },
});

export const subscriptionApi = ky.create({
  prefix: window.__CONFIG.VITE_SUBSCRIPTION_API_URL,
  timeout: 70000,
  retry: {
    limit: 1,
    methods: ['get', 'post', 'put', 'delete'],
    statusCodes: [401],
  },
  headers: {
    'X-Source': 'profile',
  },
  hooks: {
    beforeRequest: [({ request }) => setAuthHeader(request)],
    beforeRetry: [handleAuthRetry],
  },
});

export const feedbackApi = ky.create({
  prefix: window.__CONFIG.VITE_FEEDBACK_API_URL,
  timeout: 70000,
  retry: {
    limit: 1,
    methods: ['get', 'post', 'put', 'delete'],
    statusCodes: [401],
  },
  headers: {
    'X-Source': 'profile',
  },
  hooks: {
    beforeRequest: [
      ({ request }) => {
        const authToken = Cookies.get(AUTH_COOKIE_NAME);

        if (authToken) {
          setAuthHeader(request);
        } else {
          const captchaToken = localStorage.getItem('captchaToken') || '';
          request.headers.set('x-captcha-token', captchaToken);
        }
      },
    ],
    beforeRetry: [handleAuthRetry],
  },
});

export const qrApi = ky.create({
  prefix: window.__CONFIG.VITE_QR_API_URL,
  timeout: 70000,
  retry: {
    limit: 1,
    methods: ['get', 'post', 'put', 'delete'],
    statusCodes: [401],
  },
  headers: {
    'X-Source': 'profile',
  },
  hooks: {
    beforeRequest: [({ request }) => setAuthHeader(request)],
    beforeRetry: [handleAuthRetry],
  },
});
